import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useLocation, useNavigate, useRouteContext, useSearch } from "@tanstack/react-router";
import { Check, Loader2 } from "lucide-react";
import { Button, Input, LogoWordmark, cn } from "@atrix-analytics/ui";
import { api } from "../api";
import { ApiError, errorMessage } from "../api/http";
import { keys, regionsQuery } from "../api/queries";
import { CreateOrgInput, CreateProjectInput, type RegionCode } from "../api/schemas";
import { Field, FormError } from "../components/field";
import { useSignOut } from "../components/use-sign-out";

const STEPS = [
  { key: "org", label: "Organization" },
  { key: "project", label: "Project" },
  { key: "install", label: "Install the SDK" },
] as const;

export function OnboardingLayout() {
  const { pathname } = useLocation();
  const current = pathname.endsWith("/project") ? 1 : 0;
  const signOut = useSignOut();
  return (
    <div className="min-h-screen bg-bg-void">
      <header className="flex items-center justify-between px-4 py-4 sm:px-8">
        <LogoWordmark />
        <Button variant="ghost" size="sm" onClick={signOut}>
          Sign out
        </Button>
      </header>
      <main className="mx-auto w-full max-w-xl px-4 pt-6 pb-16">
        <ol className="mb-8 flex items-center gap-2 text-xs" aria-label="Setup progress">
          {STEPS.map((s, i) => (
            <li key={s.key} className="flex items-center gap-2" aria-current={i === current ? "step" : undefined}>
              <span
                className={cn(
                  "grid size-5 place-items-center rounded-full border text-[11px] font-semibold tnum",
                  i < current && "border-accent bg-accent text-accent-fg",
                  i === current && "border-accent text-accent",
                  i > current && "border-border-strong text-fg-faint",
                )}
              >
                {i < current ? <Check className="size-3" aria-hidden /> : i + 1}
              </span>
              <span className={cn(i === current ? "font-medium text-fg" : "text-fg-muted", "hidden sm:inline")}>
                {s.label}
              </span>
              {i < STEPS.length - 1 && <span aria-hidden className="h-px w-6 bg-border-strong sm:w-10" />}
            </li>
          ))}
        </ol>
        <div className="rounded-lg border border-border bg-bg-raised p-5 shadow-sm sm:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function CreateOrgPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | undefined>();
  const mutation = useMutation({
    mutationFn: api.createOrg,
    onSuccess: async (org) => {
      await qc.invalidateQueries({ queryKey: keys.me });
      await navigate({ to: "/onboarding/project", search: { org: org.id } });
    },
    onError: (err) => setError(err instanceof ApiError ? err.fields.name : undefined),
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = CreateOrgInput.safeParse(Object.fromEntries(new FormData(e.currentTarget)));
    if (!parsed.success) return setError(parsed.error.issues[0]?.message);
    setError(undefined);
    mutation.mutate(parsed.data);
  };

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-xl font-semibold tracking-tight">Name your organization</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Usually your company. Projects, billing and your team live here — you can rename it later.
        </p>
      </div>
      <FormError message={mutation.isError && !error ? errorMessage(mutation.error) : null} />
      <Field label="Organization name" error={error}>
        <Input name="name" placeholder="Acme Inc." autoComplete="organization" autoFocus required />
      </Field>
      <Button type="submit" size="lg" disabled={mutation.isPending} className="self-start">
        {mutation.isPending && <Loader2 className="animate-spin" aria-hidden />}
        Continue
      </Button>
    </form>
  );
}

const REGION_NOTES: Record<RegionCode, string> = {
  eu: "Hosted in Germany. Live now.",
  me: "In-country UAE hosting is being provisioned.",
  us: "US hosting is being provisioned.",
  pk: "In-country Pakistan hosting is pending a cloud-policy review.",
};

function timezones(): string[] {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["UTC"];
  }
}

export function CreateProjectPage() {
  const { me } = useRouteContext({ from: "/onboarding" });
  const search = useSearch({ from: "/onboarding/project" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const regions = useQuery(regionsQuery);
  const orgId = search.org ?? me.orgs[0]?.id;
  const zones = useMemo(timezones, []);
  const [region, setRegion] = useState<RegionCode>("eu");
  const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  const mutation = useMutation({
    mutationFn: (input: CreateProjectInput) => {
      if (!orgId) throw new Error("Create an organization first.");
      return api.createProject(orgId, input);
    },
    onSuccess: async ({ project, public_keys }) => {
      qc.setQueryData(keys.project(project.id), project);
      qc.setQueryData(keys.keys(project.id), public_keys);
      await qc.invalidateQueries({ queryKey: ["orgs"] });
      await navigate({ to: "/p/$projectId/install", params: { projectId: project.id } });
    },
    onError: (err) => {
      if (err instanceof ApiError) setErrors(err.fields);
    },
  });

  if (!orgId) {
    return (
      <div className="flex flex-col items-start gap-3">
        <h1 className="font-display text-xl font-semibold">Create an organization first</h1>
        <Link to="/onboarding/org" className="text-sm font-medium text-accent hover:underline">
          Name your organization
        </Link>
      </div>
    );
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = Object.fromEntries(new FormData(e.currentTarget));
    const parsed = CreateProjectInput.safeParse({ name: form.name, region, timezone });
    if (!parsed.success) {
      const out: Record<string, string> = {};
      for (const i of parsed.error.issues) out[String(i.path[0])] ??= i.message;
      return setErrors(out);
    }
    setErrors({});
    mutation.mutate(parsed.data);
  };

  const list = regions.data ?? [];

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-xl font-semibold tracking-tight">Create a project</h1>
        <p className="mt-1 text-sm text-fg-muted">
          A project is one product — an app, a site, or both. It gets its own keys for production, preview and
          development.
        </p>
      </div>
      <FormError
        message={mutation.isError && !Object.keys(errors).length ? errorMessage(mutation.error) : null}
      />
      <Field label="Project name" error={errors.name}>
        <Input name="name" placeholder="Acme web + mobile" autoFocus required />
      </Field>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1.5 text-sm font-medium">Data region</legend>
        <p className="-mt-1 mb-1 text-xs text-fg-muted">
          Where this project's events are stored and queried. It can't be changed later — moving regions means
          exporting into a new project.
        </p>
        <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Data region">
          {regions.isPending &&
            Array.from({ length: 4 }, (_, i) => <div key={i} className="h-[74px] animate-pulse rounded-md bg-bg-overlay" />)}
          {regions.isError && (
            <p className="text-sm text-danger sm:col-span-2" role="alert">
              Couldn't load regions. {errorMessage(regions.error)}{" "}
              <button type="button" className="underline" onClick={() => regions.refetch()}>
                Retry
              </button>
            </p>
          )}
          {list.map((r) => {
            const live = r.status === "live";
            const selected = region === r.code;
            return (
              <label
                key={r.code}
                className={cn(
                  "relative flex flex-col gap-0.5 rounded-md border px-3 py-2.5 text-sm transition-colors",
                  live ? "cursor-pointer" : "cursor-not-allowed opacity-70",
                  selected ? "border-accent bg-accent-soft" : "border-border-strong",
                  live && !selected && "hover:bg-bg-overlay",
                  "has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent",
                )}
              >
                <input
                  type="radio"
                  name="region"
                  value={r.code}
                  className="sr-only"
                  checked={selected}
                  disabled={!live}
                  onChange={() => setRegion(r.code)}
                  aria-describedby={`region-${r.code}-note`}
                />
                <span className="flex items-center justify-between gap-2">
                  <span className="font-medium">{r.name}</span>
                  {live ? (
                    selected && <Check className="size-4 text-accent" aria-hidden />
                  ) : (
                    <span className="rounded-sm bg-bg-inset px-1.5 py-0.5 text-[11px] text-fg-muted">Coming soon</span>
                  )}
                </span>
                <span id={`region-${r.code}-note`} className="text-xs text-fg-muted">
                  {REGION_NOTES[r.code]}
                </span>
              </label>
            );
          })}
        </div>
        {list.some((r) => r.status !== "live") && (
          <p className="text-xs text-fg-muted">
            We only open a region once its infrastructure runs in-country — a region that isn't live can't hold your
            data yet, so it can't be picked. Need one of these? Start in Europe and we'll help you move when it opens.
          </p>
        )}
        {errors.region && <p className="text-xs text-danger">{errors.region}</p>}
      </fieldset>

      <Field label="Reporting timezone" error={errors.timezone} hint="Day and week boundaries in charts use this timezone.">
        <select
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          className="h-8.5 w-full rounded-md border border-border-strong bg-bg-raised px-3 text-sm text-fg focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-soft focus-visible:outline-none"
        >
          {zones.map((z) => (
            <option key={z} value={z}>
              {z.replaceAll("_", " ")}
            </option>
          ))}
        </select>
      </Field>

      <Button type="submit" size="lg" disabled={mutation.isPending} className="self-start">
        {mutation.isPending && <Loader2 className="animate-spin" aria-hidden />}
        Create project
      </Button>
    </form>
  );
}
